/**
 * batch-57 (FO-419) - brede inhaal-migratie catalogus <-> voorraad.
 *
 * De bestaande backfill (`uitrusting-variant-voorraad-backfill.ts`, spec
 * 2026-08-25) dekt uitsluitend KLEDING/PBM-maat-varianten en matcht alleen op
 * naam. Deze module is de BREDE variant: alle vier de catalogus-categorieen
 * (KLEDING, PBM, VEILIGHEID en - nieuw - GEREEDSCHAP), beide koppel-niveaus
 * (maat-variant en artikel-zonder-maten) en een SKU/alias-ronde vóór de
 * naam-ronde.
 *
 * Bewust GEEN nieuwe matcher: de naam-ronde hergebruikt exact de bestaande
 * `artikelnaamOverlap`/`artikelnaamJaccard`/`normaliseerArtikelnaamVoorMatch` +
 * `maatMatcht` met dezelfde drempels, zodat het bestaande varianten-koppelscript
 * en deze inhaalslag nooit tegenstrijdige besluiten nemen.
 *
 * Besluit-volgorde per koppel-eenheid (spec §3.A2):
 *   0. al gekoppeld                      -> BESTAAT      (idempotent, geen SQL)
 *   0b. bewust ontkoppeld door beheer    -> ONTKOPPELD   (nooit stil terugkoppelen)
 *   1. SKU/artikelnummer of FO-362-alias -> KOPPEL (grond 'SKU')
 *   2. genormaliseerde naam + maat       -> KOPPEL (grond 'NAAM'), alleen eenduidig
 *   3. geen kandidaat                    -> NIEUW (voorraad-artikel met 0)
 *   x. meerdere gelijkwaardige kandidaten-> TWIJFEL (review-lijst, geen SQL)
 *
 * Pure kern (geen db, geen fs) zodat de tests in de deploy-gate meedraaien; de
 * CLI eromheen staat in
 * `packages/db/prisma/scripts/koppel-catalogus-voorraad-breed.ts`.
 */
import {
  artikelnaamJaccard,
  artikelnaamOverlap,
  normaliseerArtikelnaamVoorMatch,
  voorraadCategorieVoorUitrusting,
  voorraadMaatVoorVariant,
  type UitrustingCategorieCode,
} from './uitrusting-variant-voorraad'
import { isMaatloos, maatMatcht } from './uitrusting-maat'

/** Dezelfde drempel als de bestaande varianten-backfill; niet los tunen. */
const NAAM_OVERLAP_DREMPEL = 0.66

/**
 * Eén koppel-eenheid uit de catalogus-export. `kind` bepaalt WAAR de koppeling
 * landt: `VARIANT` -> `uitrusting_varianten.voorraadArtikelId` (FO-362, per
 * maat), `ARTIKEL` -> `uitrusting_artikelen.voorraadArtikelId` (FO-321, artikel
 * zonder levende maten).
 */
export interface CatalogusEenheid {
  kind: 'ARTIKEL' | 'VARIANT'
  /** Id van de rij die de koppeling krijgt (artikel-id of variant-id). */
  id: string
  /** Altijd het catalogus-artikel (ook bij kind VARIANT) - voor de audit-log. */
  artikelId: string
  artikelNaam: string
  categorie: UitrustingCategorieCode
  /** Bestelnummer bij de leverancier: variant-artikelnummer of artikel-SKU. */
  sku: string | null
  maat: string | null
  maatType: string | null
  kleur: string | null
  /** `prijsExclBtw` (variant) of `inkoopPrijs` (artikel), als decimaal-string. */
  prijs: string | null
  actief: boolean
  leverancierNaam: string | null
  omschrijving: string | null
  /**
   * De EFFECTIEVE koppeling van deze eenheid (null = ongekoppeld). Bij een
   * maat-variant is dat wat `voorraadBronVoorVariant` zou teruggeven: de eigen
   * koppeling, en anders de FO-321-koppeling van het artikel. Die erf-tak MOET
   * hier meetellen - anders krijgt de maatloze variant van een wizard-gemaakt
   * gereedschap-item een tweede, leeg voorraadartikel dat de echte bron
   * schaduwt (de variant-koppeling wint immers van de artikel-koppeling).
   */
  voorraadArtikelId: string | null
  /**
   * De RAUWE koppel-kolom van deze eenheid als die naar een SOFT-DELETED bron
   * wijst. `voorraadArtikelId` is dan `null` (er is immers geen levende bron),
   * maar de kolom in de database is dat niet - en alle koppel-statements zijn
   * geklemd op `IS NULL`. Zonder deze waarde zou de reparatie van zo'n eenheid
   * stil een no-op worden: het rapport belooft "nieuw", de SQL doet niets. De
   * SQL-bouwer maakt de kolom eerst leeg voordat hij opnieuw koppelt.
   */
  dodeKoppelingId?: string | null
  /**
   * True als `voorraadArtikelId` van het ARTIKEL komt en niet van de maat zelf.
   * Alleen voor het rapport: zo'n eenheid is "al gekoppeld" en wordt niet
   * aangeraakt, maar de lezer ziet dat de bron geërfd is (per maat splitsen is
   * een bewuste beheeractie via de catalogus-UI, geen migratie-besluit).
   */
  bronGeerfdVanArtikel?: boolean
  /**
   * Laatste koppel-log-actie voor deze eenheid was ONTKOPPELD/VARIANT_ONTKOPPELD:
   * beheer heeft de koppeling bewust losgehaald (spec §6.3). De inhaalslag laat
   * die met rust en zet 'm alleen in het rapport.
   */
  bewustOntkoppeld: boolean
}

/** Eén kandidaat-voorraadrij uit de voorraad-export. */
export interface VoorraadKandidaat {
  id: string
  artikelNummer: string
  naam: string
  maat: string | null
  kleur: string | null
  categorie: string
  /** TOTAAL saldo over alle locaties, inclusief het gereserveerde deel. */
  saldo: number
  /** Hangt er al een levend catalogus-item of een levende maat-variant aan? */
  alGekoppeld: boolean
  /** Bestelnummer bij de leverancier (Van Ysselt-SKU e.d.). */
  leverancierArtNr: string | null
  /** FO-362-aliassen: oude artikelnummers die naar deze rij resolven. */
  aliasArtikelNummers: string[]
}

export type KoppelGrond = 'SKU' | 'NAAM'

/** Besluit per koppel-eenheid. Alleen KOPPEL en NIEUW leveren SQL op. */
export type InhaalBesluit =
  | { soort: 'BESTAAT'; eenheid: CatalogusEenheid }
  | { soort: 'ONTKOPPELD'; eenheid: CatalogusEenheid }
  | { soort: 'KOPPEL'; eenheid: CatalogusEenheid; bron: VoorraadKandidaat; grond: KoppelGrond }
  | { soort: 'NIEUW'; eenheid: CatalogusEenheid }
  | {
      soort: 'TWIJFEL'
      eenheid: CatalogusEenheid
      kandidaten: VoorraadKandidaat[]
      grond: KoppelGrond
    }

/** Normaliseert een SKU/artikelnummer voor exacte vergelijking (case + witruimte + streepjes). */
export function normaliseerSku(sku: string | null | undefined): string {
  return (sku ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s._-]+/g, '')
}

/**
 * Maat-vergelijking tussen een koppel-eenheid en een voorraadkandidaat.
 * Maatloos matcht uitsluitend tegen maatloos (en andersom), anders via de
 * gedeelde `maatMatcht`. Identiek aan `maatPast` in de bestaande backfill.
 */
function maatPast(e: CatalogusEenheid, b: VoorraadKandidaat): boolean {
  const eLoos = e.maat == null || isMaatloos(e.maat)
  const bLoos = b.maat == null || isMaatloos(b.maat)
  if (eLoos || bLoos) return eLoos && bLoos
  return maatMatcht(b.maat, e.maat, e.maatType)
}

/**
 * Een voorraadnaam van minder dan twee tokens ("Werkbroek", "Boormachine") zegt
 * niets over merk of model en kan bij meerdere catalogusartikelen horen; die
 * koppelen we nooit automatisch op naam. Identiek aan de bestaande backfill.
 */
function isGeneriekeVoorraadnaam(b: VoorraadKandidaat): boolean {
  return normaliseerArtikelnaamVoorMatch(b.naam).length < 2
}

/** Jaccard-score van een kandidaat t.o.v. de catalogusnaam (0..1). */
export function inhaalKandidaatScore(e: CatalogusEenheid, b: VoorraadKandidaat): number {
  return artikelnaamJaccard(e.artikelNaam, b.naam)
}

/**
 * SKU-kandidaten: exacte match van het catalogus-bestelnummer op het
 * voorraad-`leverancierArtNr`, het voorraad-`artikelNummer` of een FO-362-alias.
 *
 * Bewust ZONDER categorie-filter: een exacte SKU-treffer is sterk bewijs, ook
 * als het magazijn de rij ooit onder ELEKTRONICA/KOFFERS/OVERIG heeft gezet.
 * De naam-ronde hieronder is juist wél categorie-gebonden (zwakker bewijs).
 */
function skuKandidaten(e: CatalogusEenheid, pool: VoorraadKandidaat[]): VoorraadKandidaat[] {
  const sku = normaliseerSku(e.sku)
  if (sku.length === 0) return []
  return pool.filter((b) => {
    if (normaliseerSku(b.leverancierArtNr) === sku) return true
    if (normaliseerSku(b.artikelNummer) === sku) return true
    return b.aliasArtikelNummers.some((a) => normaliseerSku(a) === sku)
  })
}

/**
 * Sanity-check op een SKU-treffer: botst de maat, dan koppelen we NIET stil.
 * Een alias ontstaat juist bij FO-362-merges, waar maten zijn samengevoegd; een
 * treffer op maat-54-voorraad voor een maat-52-variant is dan geen match maar
 * een verhaal voor een mens (spec §6.1). Bovendien schrijft de KOPPEL-SQL de
 * catalogus-maat op de bron - zonder deze check zou dat de fysieke maat van een
 * bestaande voorraadrij overschrijven, en het rollback-recept legt de oude maat
 * niet vast.
 */
function skuMaatBotst(e: CatalogusEenheid, b: VoorraadKandidaat): boolean {
  return !maatPast(e, b)
}

/**
 * Naam-kandidaten binnen de bij de categorie horende voorraad-categorie
 * (`voorraadCategorieVoorUitrusting`), op naam-overlap >= 0,66 én passende maat.
 */
function naamKandidaten(e: CatalogusEenheid, pool: VoorraadKandidaat[]): VoorraadKandidaat[] {
  const doelCategorie = voorraadCategorieVoorUitrusting(e.categorie)
  return pool.filter(
    (b) =>
      b.categorie === doelCategorie &&
      artikelnaamOverlap(e.artikelNaam, b.naam) >= NAAM_OVERLAP_DREMPEL &&
      maatPast(e, b),
  )
}

/**
 * Kiest uit meerdere kandidaten de ene winnaar, of `null` bij twijfel: de
 * hoogste Jaccard-score wint (die straft niet-gedeelde tokens af), een
 * gelijkspel op de hoogste score of een generieke winnaar-naam is TWIJFEL.
 * Bij een SKU-ronde telt de score niet mee: twee rijen met hetzelfde
 * bestelnummer zijn per definitie handwerk (spec §6.4).
 */
function kiesWinnaar(
  e: CatalogusEenheid,
  kandidaten: VoorraadKandidaat[],
  grond: KoppelGrond,
): VoorraadKandidaat | null {
  if (kandidaten.length === 1) {
    const enige = kandidaten[0]!
    if (grond === 'SKU') return enige
    return isGeneriekeVoorraadnaam(enige) ? null : enige
  }
  if (grond === 'SKU') return null
  const hoogste = Math.max(...kandidaten.map((b) => inhaalKandidaatScore(e, b)))
  // Floating point: scores zijn breuken, dus vergelijk met een marge.
  const winnaars = kandidaten.filter((b) => Math.abs(inhaalKandidaatScore(e, b) - hoogste) < 1e-9)
  if (winnaars.length !== 1) return null
  const winnaar = winnaars[0]!
  return isGeneriekeVoorraadnaam(winnaar) ? null : winnaar
}

/**
 * Bepaalt per koppel-eenheid het besluit. Een voorraadbron wordt maar één keer
 * geclaimd, ook binnen dezelfde run (volgorde van `eenheden` bepaalt wie een
 * gedeelde bron krijgt) - net als in de bestaande backfill.
 */
export function bepaalInhaalBesluiten(
  eenheden: CatalogusEenheid[],
  voorraad: VoorraadKandidaat[],
): InhaalBesluit[] {
  const geclaimd = new Set(voorraad.filter((b) => b.alGekoppeld).map((b) => b.id))
  const besluiten: InhaalBesluit[] = []
  for (const e of eenheden) {
    if (e.voorraadArtikelId) {
      besluiten.push({ soort: 'BESTAAT', eenheid: e })
      continue
    }
    if (e.bewustOntkoppeld) {
      besluiten.push({ soort: 'ONTKOPPELD', eenheid: e })
      continue
    }
    const vrij = voorraad.filter((b) => !geclaimd.has(b.id))

    const perSku = skuKandidaten(e, vrij)
    if (perSku.length > 0) {
      // Alleen een SKU-treffer met een PASSENDE maat mag automatisch koppelen;
      // een maat-botsing gaat naar de review-lijst i.p.v. stil de bron-maat te
      // overschrijven.
      const passend = perSku.filter((b) => !skuMaatBotst(e, b))
      const winnaar = passend.length > 0 ? kiesWinnaar(e, passend, 'SKU') : null
      if (winnaar) {
        geclaimd.add(winnaar.id)
        besluiten.push({ soort: 'KOPPEL', eenheid: e, bron: winnaar, grond: 'SKU' })
      } else {
        besluiten.push({ soort: 'TWIJFEL', eenheid: e, kandidaten: perSku, grond: 'SKU' })
      }
      continue
    }

    const perNaam = naamKandidaten(e, vrij)
    if (perNaam.length === 0) {
      besluiten.push({ soort: 'NIEUW', eenheid: e })
      continue
    }
    const winnaar = kiesWinnaar(e, perNaam, 'NAAM')
    if (winnaar) {
      geclaimd.add(winnaar.id)
      besluiten.push({ soort: 'KOPPEL', eenheid: e, bron: winnaar, grond: 'NAAM' })
    } else {
      besluiten.push({ soort: 'TWIJFEL', eenheid: e, kandidaten: perNaam, grond: 'NAAM' })
    }
  }
  return besluiten
}

// ── SQL-generatie ───────────────────────────────────────────────────────────

/** SQL-string-literal, of `NULL`; verdubbelt enkele quotes. */
function sqlStr(s: string | null): string {
  return s == null ? 'NULL' : `'${s.replace(/'/g, "''")}'`
}

/**
 * Genereert een uniek id voor een gegenereerde SQL-rij. Structureel getypeerd op
 * `globalThis.crypto` (GEEN `node:crypto`-import) zodat dit bestand, dat via de
 * barrel ook door de frontends geladen wordt, in hun bundels blijft compileren.
 */
function nieuwId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const rnd = () =>
    Math.floor(Math.random() * 0xffffffff)
      .toString(16)
      .padStart(8, '0')
  return `id-${Date.now().toString(16)}-${rnd()}-${rnd()}`
}

/**
 * QR-token voor een gegenereerd voorraadartikel: 16 random bytes als base64url
 * (22 tekens), exact de vorm van `genereerQrToken` in de api (`lib/qr.ts`).
 * Structureel getypeerd op `globalThis.crypto`, zelfde reden als `nieuwId`.
 */
function nieuwQrToken(): string {
  const bytes = new Uint8Array(16)
  const c = (globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array } }).crypto
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  let binair = ''
  for (const b of bytes) binair += String.fromCharCode(b)
  return btoa(binair).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Alleen een kaal decimaal literal is veilig om ongequote te interpoleren. */
const DECIMAAL_PATROON = /^\d+(\.\d+)?$/

/**
 * Guard: een corrupte export (geen kaal decimaal literal) mag nooit
 * ongevalideerd de SQL in - dan liever hard falen met de eenheid-id.
 */
function veiligePrijs(e: CatalogusEenheid): string {
  if (e.prijs == null || e.prijs.trim().length === 0) return 'NULL'
  if (!DECIMAAL_PATROON.test(e.prijs)) {
    throw new Error(`Ongeldige prijs "${e.prijs}" voor ${e.kind} ${e.id} - corrupte export?`)
  }
  return e.prijs
}

/** Rollback-informatie bij een run (spec §9): wat is aangemaakt en wat gekoppeld. */
export interface InhaalRollback {
  /** Ids van de door deze run aangemaakte voorraad-artikelen. */
  nieuweVoorraadArtikelIds: string[]
  /** Eenheden die een koppeling kregen (voor het terugzetten op NULL). */
  gekoppeldeEenheden: Array<{ kind: 'ARTIKEL' | 'VARIANT'; id: string }>
}

export interface InhaalSqlResultaat {
  sql: string
  rollback: InhaalRollback
}

/**
 * Maakt een koppel-kolom leeg die naar een SOFT-DELETED bron wijst, zodat de
 * `IS NULL`-klem daarna kan doen wat het rapport belooft. Alleen nodig als de
 * export een dode koppeling meldt; anders levert dit geen regel op.
 */
function maakDodeKoppelingLeegSql(e: CatalogusEenheid): string | null {
  const dood = e.dodeKoppelingId
  if (!dood) return null
  return `UPDATE ${eenheidTabel(e)} SET "voorraadArtikelId" = NULL WHERE id = ${sqlStr(e.id)} AND "voorraadArtikelId" = ${sqlStr(dood)};`
}

function updateKoppelingSql(e: CatalogusEenheid, bronId: string): string {
  // Op ARTIKELniveau erft het catalogus-item normaal zijn presentatievelden uit
  // de bron (FO-321) en wordt het daarmee read-only. Deze koppeling dient ALLEEN
  // de voorraad - de bron is uit dit item gemaakt of er automatisch aan gematcht -
  // dus `voorraadBronErft` gaat op false en het item blijft bewerkbaar (FO-419).
  // Een maat-variant kent die erf-regel niet en heeft de kolom dus ook niet.
  const extra = e.kind === 'ARTIKEL' ? ', "voorraadBronErft" = false' : ''
  return `UPDATE ${eenheidTabel(e)} SET "voorraadArtikelId" = ${sqlStr(bronId)}${extra} WHERE id = ${sqlStr(e.id)} AND "voorraadArtikelId" IS NULL;`
}

/** Tabel waarin de koppeling van deze eenheid leeft. */
function eenheidTabel(e: CatalogusEenheid): string {
  return e.kind === 'VARIANT' ? 'uitrusting_varianten' : 'uitrusting_artikelen'
}

/**
 * `EXISTS`-guard: is de koppeling van deze eenheid ECHT gelegd (of nog vrij)?
 *
 * De export is een momentopname. Koppelt iemand in de tussentijd via de UI, dan
 * no-opt de geklemde UPDATE - maar zonder deze guards zouden de INSERTs ernaast
 * gewoon doorlopen: een leeg voorraadartikel dat niemand gebruikt, plus een
 * koppel-log-rij die een koppeling claimt die nooit is gelegd. Met de guard doet
 * zo'n eenheid helemaal niets meer.
 */
function eenheidGuard(e: CatalogusEenheid, conditie: string): string {
  return `EXISTS (SELECT 1 FROM ${eenheidTabel(e)} WHERE id = ${sqlStr(e.id)} AND ${conditie})`
}

function koppelLogSql(
  e: CatalogusEenheid,
  bronId: string,
  actie: 'AUTO_GEKOPPELD' | 'AUTO_AANGEMAAKT',
  actorUserId: string | null,
): string {
  const variantId = e.kind === 'VARIANT' ? sqlStr(e.id) : 'NULL'
  const guard = eenheidGuard(e, `"voorraadArtikelId" = ${sqlStr(bronId)}`)
  return `INSERT INTO uitrusting_artikel_koppel_logs (id, "artikelId", "variantId", "voorraadArtikelId", actie, "actorUserId") SELECT ${sqlStr(nieuwId())}, ${sqlStr(e.artikelId)}, ${variantId}, ${sqlStr(bronId)}, '${actie}'::catalogus_koppel_actie, ${sqlStr(actorUserId)} WHERE ${guard};`
}

/**
 * Bouwt de SQL voor alle KOPPEL/NIEUW-besluiten als één transactie
 * (BEGIN/COMMIT). Nieuwe artikelnummers lopen door vanaf `maxArt`
 * (ART-<maxArt+1>, ...). Elke wijziging krijgt een koppel-log-rij.
 * BESTAAT/ONTKOPPELD/TWIJFEL genereren geen SQL.
 *
 * Idempotent bij herdraaien: elke koppel-UPDATE is geklemd op
 * `"voorraadArtikelId" IS NULL`, en de INSERTs ernaast (het nieuwe voorraad-
 * artikel en de koppel-log-rij) staan achter een `EXISTS`-guard op diezelfde
 * eenheid. Is er tussen export en run alsnog handmatig gekoppeld, dan doet de
 * hele eenheid niets - geen weesartikel, geen log-rij die een koppeling claimt
 * die er niet is. Draai bij twijfel opnieuw de dry-run: die ziet de eenheid dan
 * als BESTAAT en genereert niets meer.
 *
 * Enum-waarden worden expliciet gecast (`::voorraad_categorie`,
 * `::voorraad_eenheid`, `::catalogus_koppel_actie`): in een `INSERT ... SELECT`
 * is een kaal literal `unknown` en laten we de typeresolutie liever niet aan het
 * toeval over.
 */
export function bouwInhaalSql(
  besluiten: InhaalBesluit[],
  maxArt: number,
  actorUserId: string | null,
): InhaalSqlResultaat {
  let volgnr = maxArt
  const regels: string[] = ['BEGIN;']
  const rollback: InhaalRollback = { nieuweVoorraadArtikelIds: [], gekoppeldeEenheden: [] }

  for (const b of besluiten) {
    if (b.soort === 'KOPPEL') {
      const e = b.eenheid
      const prijs = veiligePrijs(e)
      const setDelen: string[] = []
      // De maat op het voorraadartikel volgt de catalogus (één maat-waarheid) -
      // maar alleen bij een MAAT-variant. Een artikel-koppeling zegt niets over
      // maten, dus die laat de bestaande maat op de voorraadrij staan (een
      // SKU-treffer kan een rij mét maat zijn; die leegmaken vernietigt data).
      if (e.kind === 'VARIANT') {
        setDelen.push(`maat = ${sqlStr(voorraadMaatVoorVariant(e.maat, e.maatType))}`)
      }
      // Uit voorraad geleverde spullen boeken op de VOORRAADprijs (FO-335): een
      // lege inkoopprijs vullen we eenmalig met de catalogusprijs, een bestaande
      // blijft staan (COALESCE - het magazijn is daar baas).
      if (prijs !== 'NULL') {
        setDelen.push(`"inkoopPrijs" = COALESCE("inkoopPrijs", ${prijs})`)
      }
      const losmakenKoppel = maakDodeKoppelingLeegSql(e)
      if (losmakenKoppel) regels.push(losmakenKoppel)
      regels.push(updateKoppelingSql(e, b.bron.id))
      if (setDelen.length > 0) {
        // Alleen aanpassen als de koppeling daadwerkelijk gelegd is (stale export).
        const guard = eenheidGuard(e, `"voorraadArtikelId" = ${sqlStr(b.bron.id)}`)
        regels.push(
          `UPDATE voorraad_artikelen SET ${setDelen.join(', ')} WHERE id = ${sqlStr(b.bron.id)} AND ${guard};`,
        )
      }
      regels.push(koppelLogSql(e, b.bron.id, 'AUTO_GEKOPPELD', actorUserId))
      rollback.gekoppeldeEenheden.push({ kind: e.kind, id: e.id })
      continue
    }
    if (b.soort === 'NIEUW') {
      const e = b.eenheid
      const prijs = veiligePrijs(e)
      volgnr += 1
      const id = nieuwId()
      const nummer = `ART-${String(volgnr).padStart(4, '0')}`
      const maat = voorraadMaatVoorVariant(e.maat, e.maatType)
      // Bewust altijd actief: een inactief catalogus-item mag best een actief
      // voorraadartikel hebben (het magazijn beheert dat zelf).
      // Ook de INSERT staat achter de guard: is de eenheid intussen gekoppeld
      // (stale export), dan mag er geen weesartikel achterblijven.
      const losmakenNieuw = maakDodeKoppelingLeegSql(e)
      if (losmakenNieuw) regels.push(losmakenNieuw)
      const nogVrij = eenheidGuard(e, '"voorraadArtikelId" IS NULL')
      regels.push(
        `INSERT INTO voorraad_artikelen (id, "artikelNummer", naam, categorie, eenheid, maat, kleur, omschrijving, leverancier, "leverancierArtNr", "inkoopPrijs", "minimumVoorraad", actief, traceerbaar, bestelbaar, "qrToken", "createdAt", "updatedAt") SELECT ${sqlStr(id)}, ${sqlStr(nummer)}, ${sqlStr(e.artikelNaam)}, ${sqlStr(voorraadCategorieVoorUitrusting(e.categorie))}::voorraad_categorie, 'STUK'::voorraad_eenheid, ${sqlStr(maat)}, ${sqlStr(e.kleur)}, ${sqlStr(e.omschrijving)}, ${sqlStr(e.leverancierNaam)}, ${sqlStr(e.sku)}, ${prijs}, 0, true, false, false, ${sqlStr(nieuwQrToken())}, now(), now() WHERE ${nogVrij};`,
        updateKoppelingSql(e, id),
        koppelLogSql(e, id, 'AUTO_AANGEMAAKT', actorUserId),
      )
      rollback.nieuweVoorraadArtikelIds.push(id)
      rollback.gekoppeldeEenheden.push({ kind: e.kind, id: e.id })
    }
  }
  regels.push('COMMIT;')
  regels.push('', ...rollbackCommentaar(rollback))
  return { sql: regels.join('\n'), rollback }
}

/**
 * Rollback-recept als SQL-commentaar onderaan het bestand (spec §9): de
 * koppelingen terug op NULL en de aangemaakte artikelen soft-deleten. Bewust
 * commentaar en geen uitvoerbaar statement - terugdraaien is een bewuste actie.
 */
export function rollbackCommentaar(rollback: InhaalRollback): string[] {
  const varianten = rollback.gekoppeldeEenheden.filter((g) => g.kind === 'VARIANT').map((g) => g.id)
  const artikelen = rollback.gekoppeldeEenheden.filter((g) => g.kind === 'ARTIKEL').map((g) => g.id)
  const regels = [
    '-- ── ROLLBACK-RECEPT (niet automatisch uitgevoerd) ───────────────────────',
    `-- Aangemaakte voorraad-artikelen: ${rollback.nieuweVoorraadArtikelIds.length}`,
    `-- Gekoppelde maat-varianten: ${varianten.length} · gekoppelde artikelen: ${artikelen.length}`,
    '-- BEGIN;',
  ]
  if (varianten.length > 0) {
    regels.push(
      `--   UPDATE uitrusting_varianten SET "voorraadArtikelId" = NULL WHERE id IN (${varianten.map((id) => sqlStr(id)).join(', ')});`,
    )
  }
  if (artikelen.length > 0) {
    regels.push(
      `--   UPDATE uitrusting_artikelen SET "voorraadArtikelId" = NULL, "voorraadBronErft" = true WHERE id IN (${artikelen.map((id) => sqlStr(id)).join(', ')});`,
    )
  }
  if (rollback.nieuweVoorraadArtikelIds.length > 0) {
    regels.push(
      `--   UPDATE voorraad_artikelen SET "deletedAt" = now() WHERE id IN (${rollback.nieuweVoorraadArtikelIds.map((id) => sqlStr(id)).join(', ')});`,
    )
  }
  regels.push('-- COMMIT;')
  return regels
}

// ── Rapport ─────────────────────────────────────────────────────────────────

function score(e: CatalogusEenheid, b: VoorraadKandidaat): string {
  return inhaalKandidaatScore(e, b).toFixed(2)
}

function eenheidLabel(e: CatalogusEenheid): string {
  const maat = e.maat && e.maat.trim().length > 0 ? e.maat : e.kind === 'VARIANT' ? '-' : 'geen maat'
  return `${maat}${e.sku ? ` [${e.sku}]` : ''}`
}

/**
 * Mensleesbaar dry-run-rapport, gegroepeerd per catalogus-artikel: een kop met
 * de totalen (koppelen/nieuw/twijfel/al gekoppeld/bewust ontkoppeld), per
 * artikel een regel per koppel-eenheid met de uitkomst (bij KOPPEL en TWIJFEL
 * mét grond en score), daarna de expliciete review-lijst en onderaan de
 * voorraadrijen die bij geen enkele eenheid horen. Die weeslijst is bewust
 * zichtbaar: die rijen blijven staan en verklaren waarom een deel van de
 * voorraad na de inhaalslag niet bestelbaar is.
 */
export function inhaalRapport(besluiten: InhaalBesluit[], voorraad: VoorraadKandidaat[]): string {
  const telling = { BESTAAT: 0, ONTKOPPELD: 0, KOPPEL: 0, NIEUW: 0, TWIJFEL: 0 }
  const perCategorie = new Map<string, { KOPPEL: number; NIEUW: number; TWIJFEL: number }>()
  const perArtikel = new Map<string, string[]>()
  const geclaimd = new Set<string>()
  const review: string[] = []

  for (const b of besluiten) {
    telling[b.soort] += 1
    const e = b.eenheid
    if (b.soort === 'KOPPEL') geclaimd.add(b.bron.id)
    if (b.soort === 'KOPPEL' || b.soort === 'NIEUW' || b.soort === 'TWIJFEL') {
      const c = perCategorie.get(e.categorie) ?? { KOPPEL: 0, NIEUW: 0, TWIJFEL: 0 }
      c[b.soort] += 1
      perCategorie.set(e.categorie, c)
    }
    const regel =
      b.soort === 'KOPPEL'
        ? `${eenheidLabel(e)} -> ${b.bron.artikelNummer} (${b.bron.naam}, totaal ${b.bron.saldo}, via ${b.grond}, score ${score(e, b.bron)})`
        : b.soort === 'NIEUW'
          ? `${eenheidLabel(e)} -> NIEUW (voorraad 0)`
          : b.soort === 'TWIJFEL'
            ? `${eenheidLabel(e)} -> TWIJFEL (${b.grond}): ${b.kandidaten
                .map((k) => `${k.artikelNummer} (${k.naam} ${k.maat ?? ''}, score ${score(e, k)})`)
                .join(' | ')}`
            : b.soort === 'ONTKOPPELD'
              ? `${eenheidLabel(e)} -> bewust ontkoppeld (overgeslagen)`
              : `${eenheidLabel(e)} -> al gekoppeld${e.bronGeerfdVanArtikel ? ' (bron geërfd van het artikel)' : ''}`
    if (b.soort === 'TWIJFEL') {
      review.push(`  ${e.artikelNaam} · ${regel}`)
    }
    const lijst = perArtikel.get(e.artikelNaam) ?? []
    lijst.push(regel)
    perArtikel.set(e.artikelNaam, lijst)
  }

  const wezen = voorraad.filter((b) => !b.alGekoppeld && !geclaimd.has(b.id))
  const kop =
    `Inhaalslag catalogus-voorraadkoppeling (alle categorieen)\n` +
    `${telling.KOPPEL} koppelen, ${telling.NIEUW} nieuw, ${telling.TWIJFEL} twijfel, ` +
    `${telling.BESTAAT} al gekoppeld, ${telling.ONTKOPPELD} bewust ontkoppeld\n`
  const perCat =
    '\nPer categorie (koppelen / nieuw / twijfel):' +
    [...perCategorie.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'nl'))
      .map(([cat, c]) => `\n  ${cat}: ${c.KOPPEL} / ${c.NIEUW} / ${c.TWIJFEL}`)
      .join('')
  const body = [...perArtikel.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'nl'))
    .map(([naam, regels]) => `\n${naam}\n  ${regels.join('\n  ')}`)
    .join('\n')
  const reviewBlok =
    `\n\nReview-lijst (handmatig beslissen, niets gewijzigd): ${review.length}` +
    (review.length > 0 ? `\n${review.join('\n')}` : '')
  const staart =
    `\n\nVoorraadrijen zonder catalogus-tegenhanger (blijven staan): ${wezen.length}` +
    wezen
      .map((w) => `\n  ${w.artikelNummer} · ${w.naam} · ${w.maat ?? '-'} · totaal ${w.saldo}`)
      .join('')
  return kop + perCat + '\n' + body + reviewBlok + staart
}
